import { useState, useEffect } from 'react'
import ScheduleCalendar from '../../components/admin/ScheduleCalendar'
import { createLesson, supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { X, Plus, Tent, Users, Calendar, Trash2 } from 'lucide-react'
import { format, parseISO } from 'date-fns'

const LESSON_TYPES = ['beginner','intermediate','advanced','kids','private','group']
const SKILL_OPTIONS = ['beginner','intermediate','advanced','kids']

// ─── Camp Modal ───────────────────────────────────────────────────────────────
function CampModal({ onClose, onSaved, instructors, editCamp }) {
  const { user } = useAuth()

  const [form, setForm] = useState(editCamp ? {
    name: editCamp.name,
    start_date: editCamp.start_date,
    end_date: editCamp.end_date,
    max_kids: editCamp.max_kids,
    skill_levels: editCamp.skill_levels || [],
    notes: editCamp.notes || '',
  } : {
    name: '', start_date: '', end_date: '',
    max_kids: 10, skill_levels: [], notes: '',
  })

  // sessions: [{ tempId, session_date, start_time, end_time, instructor_ids[] }]
  const [sessions, setSessions] = useState(() => {
    if (!editCamp?.surf_camp_sessions) return []
    return editCamp.surf_camp_sessions.map(s => ({
      tempId: s.id,
      session_date: s.session_date,
      start_time: s.start_time.slice(0, 5),
      end_time: s.end_time.slice(0, 5),
      instructor_ids: (s.surf_camp_session_instructors || []).map(si => si.instructor_id),
    }))
  })

  const [showAddSession, setShowAddSession] = useState(false)
  const [newSession, setNewSession] = useState({ session_date: '', start_time: '09:00', end_time: '11:00', instructor_ids: [] })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const set = k => v => setForm(f => ({ ...f, [k]: v }))

  const addSkill = (val) => {
    if (!val || form.skill_levels.includes(val)) return
    set('skill_levels')([...form.skill_levels, val])
  }
  const removeSkill = (val) => set('skill_levels')(form.skill_levels.filter(s => s !== val))

  const toggleNewSessionInstructor = (id) =>
    setNewSession(s => ({
      ...s,
      instructor_ids: s.instructor_ids.includes(id)
        ? s.instructor_ids.filter(i => i !== id)
        : [...s.instructor_ids, id]
    }))

  const commitAddSession = () => {
    if (!newSession.session_date || !newSession.start_time || !newSession.end_time) return
    setSessions(s => [...s, { ...newSession, tempId: `tmp-${Date.now()}` }])
    setNewSession({ session_date: '', start_time: '09:00', end_time: '11:00', instructor_ids: [] })
    setShowAddSession(false)
  }

  const removeSession = (tempId) => setSessions(s => s.filter(sess => sess.tempId !== tempId))

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form.name || !form.start_date || !form.end_date) { setError('Name and dates are required.'); return }
    if (form.start_date > form.end_date) { setError('End date must be after start date.'); return }
    setSaving(true); setError('')

    const { data: profile } = await supabase.from('profiles').select('school_id').eq('id', user.id).single()
    if (!profile?.school_id) { setError('No school found.'); setSaving(false); return }

    let campId = editCamp?.id
    if (editCamp) {
      const { error: upErr } = await supabase.from('surf_camps').update({ ...form }).eq('id', campId)
      if (upErr) { setError(upErr.message); setSaving(false); return }
    } else {
      const { data, error: insErr } = await supabase.from('surf_camps')
        .insert({ ...form, school_id: profile.school_id, enrolled_kids: 0, status: 'active' })
        .select().single()
      if (insErr) { setError(insErr.message); setSaving(false); return }
      campId = data.id
    }

    // Replace all sessions
    await supabase.from('surf_camp_sessions').delete().eq('camp_id', campId)
    for (const sess of sessions) {
      const { data: sessRow } = await supabase.from('surf_camp_sessions').insert({
        camp_id: campId,
        session_date: sess.session_date,
        start_time: sess.start_time,
        end_time: sess.end_time,
      }).select().single()
      if (sessRow && sess.instructor_ids.length > 0) {
        await supabase.from('surf_camp_session_instructors').insert(
          sess.instructor_ids.map(iid => ({ session_id: sessRow.id, instructor_id: iid }))
        )
      }
    }

    setSaving(false)
    onSaved()
    onClose()
  }

  const availableSkills = SKILL_OPTIONS.filter(s => !form.skill_levels.includes(s))

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 580 }}>
        <div className="modal-header">
          <h2 className="modal-title">{editCamp ? 'Edit Surf Camp' : 'New Surf Camp'}</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ padding: 6 }}><X size={16} /></button>
        </div>

        <form onSubmit={handleSave}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

            {/* Name */}
            <div className="form-group">
              <label className="form-label">Camp Name</label>
              <input className="form-control" placeholder="e.g. Summer Surf Camp 2025"
                value={form.name} onChange={e => set('name')(e.target.value)} required />
            </div>

            {/* Date range + max kids */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 100px', gap: '0.75rem' }}>
              <div className="form-group">
                <label className="form-label">Start Date</label>
                <input className="form-control" type="date" value={form.start_date}
                  onChange={e => set('start_date')(e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">End Date</label>
                <input className="form-control" type="date" value={form.end_date}
                  onChange={e => set('end_date')(e.target.value)} required />
              </div>
              <div className="form-group">
                <label className="form-label">Max Kids</label>
                <input className="form-control" type="number" min={1} max={500}
                  value={form.max_kids} onChange={e => set('max_kids')(parseInt(e.target.value) || 1)} />
              </div>
            </div>

            {/* Skill levels */}
            <div className="form-group">
              <label className="form-label">Skill Levels</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: form.skill_levels.length ? 8 : 0 }}>
                {form.skill_levels.map(s => (
                  <span key={s} style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    background: 'rgba(14,170,164,0.1)', border: '1px solid rgba(14,170,164,0.3)',
                    borderRadius: 20, padding: '3px 10px 3px 12px',
                    fontSize: '0.8rem', fontWeight: 600, color: 'var(--teal)', textTransform: 'capitalize',
                  }}>
                    {s}
                    <button type="button" onClick={() => removeSkill(s)} style={{
                      background: 'none', border: 'none', cursor: 'pointer', color: 'var(--teal)',
                      padding: 0, display: 'flex', lineHeight: 1,
                    }}><X size={12} /></button>
                  </span>
                ))}
              </div>
              {availableSkills.length > 0 && (
                <select className="form-control" value=""
                  onChange={e => { addSkill(e.target.value); e.target.value = '' }}>
                  <option value="">+ Add a skill level...</option>
                  {availableSkills.map(s => (
                    <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                  ))}
                </select>
              )}
            </div>

            {/* Notes */}
            <div className="form-group">
              <label className="form-label">Notes (optional)</label>
              <textarea className="form-control" rows={2} style={{ resize: 'vertical' }}
                placeholder="Equipment, meeting point, special instructions..."
                value={form.notes} onChange={e => set('notes')(e.target.value)} />
            </div>

            {/* ── Daily Schedule ── */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                <label className="form-label" style={{ margin: 0 }}>
                  Daily Schedule
                  <span style={{ marginLeft: 6, fontSize: '0.72rem', color: 'var(--text-light)', fontWeight: 400 }}>
                    ({sessions.length} day{sessions.length !== 1 ? 's' : ''})
                  </span>
                </label>
                {!showAddSession && (
                  <button type="button" className="btn btn-ghost btn-sm"
                    onClick={() => setShowAddSession(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.78rem' }}>
                    <Plus size={13} /> Add Day
                  </button>
                )}
              </div>

              {/* Existing sessions */}
              {sessions.length === 0 && !showAddSession && (
                <div style={{
                  padding: '0.875rem', background: 'rgba(11,30,61,0.03)',
                  border: '1px dashed rgba(11,30,61,0.15)', borderRadius: 8,
                  fontSize: '0.825rem', color: 'var(--text-light)', textAlign: 'center',
                }}>
                  No days added yet. Click <strong>Add Day</strong> to schedule camp sessions.
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {sessions
                  .slice()
                  .sort((a, b) => a.session_date.localeCompare(b.session_date))
                  .map(sess => {
                    const instrNames = sess.instructor_ids.map(iid => {
                      const inst = instructors.find(i => i.id === iid)
                      return inst?.profile?.full_name || '?'
                    })
                    return (
                      <div key={sess.tempId} style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        background: 'rgba(212,168,75,0.06)', border: '1px solid rgba(212,168,75,0.25)',
                        borderRadius: 8, padding: '8px 12px',
                      }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-dark)' }}>
                            {format(parseISO(sess.session_date), 'EEE, MMM d')}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-light)', marginTop: 1 }}>
                            {sess.start_time} – {sess.end_time}
                            {instrNames.length > 0 && (
                              <span style={{ marginLeft: 8, color: 'var(--text-mid)' }}>
                                · {instrNames.join(', ')}
                              </span>
                            )}
                            {instrNames.length === 0 && (
                              <span style={{ marginLeft: 8, fontStyle: 'italic', color: 'var(--text-light)' }}>
                                No instructors assigned
                              </span>
                            )}
                          </div>
                        </div>
                        <button type="button" onClick={() => removeSession(sess.tempId)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-light)', padding: 4, display: 'flex' }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )
                  })}
              </div>

              {/* Add session form */}
              {showAddSession && (
                <div style={{
                  marginTop: 8, background: 'white',
                  border: '1.5px solid rgba(14,170,164,0.3)', borderRadius: 10, padding: '1rem',
                }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 90px', gap: '0.75rem', marginBottom: '0.875rem' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Date</label>
                      <input className="form-control" type="date"
                        min={form.start_date || undefined}
                        max={form.end_date || undefined}
                        value={newSession.session_date}
                        onChange={e => setNewSession(s => ({ ...s, session_date: e.target.value }))} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Start</label>
                      <input className="form-control" type="time" value={newSession.start_time}
                        onChange={e => setNewSession(s => ({ ...s, start_time: e.target.value }))} />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">End</label>
                      <input className="form-control" type="time" value={newSession.end_time}
                        onChange={e => setNewSession(s => ({ ...s, end_time: e.target.value }))} />
                    </div>
                  </div>

                  {/* Instructor selection */}
                  <div style={{ marginBottom: '0.875rem' }}>
                    <label className="form-label">Instructors for this day</label>
                    {instructors.length === 0 ? (
                      <p style={{ fontSize: '0.8rem', color: 'var(--text-light)' }}>No approved instructors yet.</p>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {instructors.map(inst => {
                          const checked = newSession.instructor_ids.includes(inst.id)
                          return (
                            <button key={inst.id} type="button"
                              onClick={() => toggleNewSessionInstructor(inst.id)}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                padding: '5px 12px', borderRadius: 20, border: '1.5px solid',
                                borderColor: checked ? 'var(--teal)' : 'rgba(11,30,61,0.15)',
                                background: checked ? 'rgba(14,170,164,0.1)' : 'white',
                                cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600,
                                color: checked ? 'var(--teal)' : 'var(--text-mid)',
                                fontFamily: 'DM Sans, sans-serif', transition: 'all 0.15s',
                              }}>
                              {checked && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--teal)', flexShrink: 0 }} />}
                              {inst.profile?.full_name?.split(' ')[0]}
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" className="btn btn-primary btn-sm" onClick={commitAddSession}
                      disabled={!newSession.session_date}>
                      Add Day
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm"
                      onClick={() => { setShowAddSession(false); setNewSession({ session_date: '', start_time: '09:00', end_time: '11:00', instructor_ids: [] }) }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {error && <p style={{ color: 'var(--coral)', fontSize: '0.8rem', margin: 0 }}>{error}</p>}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editCamp ? 'Save Changes' : 'Create Camp'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Camp Card ────────────────────────────────────────────────────────────────
function CampCard({ camp, instructors, onEdit, onCancel }) {
  const sessions = (camp.surf_camp_sessions || [])
    .slice()
    .sort((a, b) => a.session_date.localeCompare(b.session_date))

  return (
    <div style={{
      background: 'white', border: '1px solid rgba(212,168,75,0.3)',
      borderLeft: '3px solid #D4A84B', borderRadius: 10, padding: '1rem 1.25rem',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
            <Tent size={15} color="#D4A84B" />
            <h3 style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-dark)', margin: 0 }}>{camp.name}</h3>
          </div>
          <div style={{ fontSize: '0.775rem', color: 'var(--text-light)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Calendar size={11} />
            {format(parseISO(camp.start_date), 'MMM d')} – {format(parseISO(camp.end_date), 'MMM d, yyyy')}
            <span style={{ margin: '0 4px' }}>·</span>
            <Users size={11} />
            {camp.enrolled_kids}/{camp.max_kids} kids
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {camp.skill_levels?.map(s => (
            <span key={s} style={{
              background: 'rgba(11,30,61,0.06)', color: 'var(--text-mid)',
              borderRadius: 20, padding: '2px 8px', fontSize: '0.7rem',
              fontWeight: 600, textTransform: 'capitalize',
            }}>{s}</span>
          ))}
        </div>
      </div>

      {/* Sessions list */}
      {sessions.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
          {sessions.map(sess => {
            const instrNames = (sess.surf_camp_session_instructors || []).map(si => {
              const inst = instructors.find(i => i.id === si.instructor_id)
              return inst?.profile?.full_name?.split(' ')[0] || '?'
            })
            return (
              <div key={sess.id} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                fontSize: '0.775rem', color: 'var(--text-mid)',
                background: 'rgba(212,168,75,0.06)', borderRadius: 6,
                padding: '4px 8px',
              }}>
                <span style={{ fontWeight: 600, color: 'var(--text-dark)', minWidth: 80 }}>
                  {format(parseISO(sess.session_date), 'EEE, MMM d')}
                </span>
                <span>{sess.start_time?.slice(0,5)} – {sess.end_time?.slice(0,5)}</span>
                {instrNames.length > 0 && (
                  <span style={{ color: 'var(--text-light)' }}>· {instrNames.join(', ')}</span>
                )}
                {instrNames.length === 0 && (
                  <span style={{ color: 'var(--coral)', fontStyle: 'italic', fontSize: '0.72rem' }}>No instructors</span>
                )}
              </div>
            )
          })}
        </div>
      )}

      {sessions.length === 0 && (
        <p style={{ fontSize: '0.775rem', color: 'var(--text-light)', fontStyle: 'italic', marginBottom: 10 }}>
          No sessions scheduled yet.
        </p>
      )}

      {camp.notes && (
        <p style={{ fontSize: '0.775rem', color: 'var(--text-light)', marginBottom: 10 }}>{camp.notes}</p>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-ghost btn-sm" onClick={() => onEdit(camp)}>Edit Camp</button>
        <button className="btn btn-ghost btn-sm" onClick={() => onCancel(camp.id)}
          style={{ color: 'var(--coral)', borderColor: 'rgba(232,97,74,0.3)' }}>
          Cancel
        </button>
      </div>
    </div>
  )
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function AdminSchedule() {
  const { user } = useAuth()
  const [showCreateLesson, setShowCreateLesson] = useState(false)
  const [showCreateCamp, setShowCreateCamp] = useState(false)
  const [editCamp, setEditCamp] = useState(null)
  const [camps, setCamps] = useState([])
  const [instructors, setInstructors] = useState([])
  const [schoolId, setSchoolId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0)
  const [form, setForm] = useState({
    lesson_type: 'beginner', location: '',
    scheduled_date: '', start_time: '09:00', end_time: '10:00',
    num_students: 1, skill_level: 'beginner', notes: '', price: 75
  })

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  useEffect(() => { if (user) loadCamps() }, [user])

  const loadCamps = async () => {
    const { data: profile } = await supabase.from('profiles').select('school_id').eq('id', user.id).single()
    if (!profile?.school_id) return
    setSchoolId(profile.school_id)

    const [{ data: campsData }, { data: instrData }] = await Promise.all([
      supabase.from('surf_camps')
        .select(`
          *,
          surf_camp_sessions(
            id, session_date, start_time, end_time,
            surf_camp_session_instructors(instructor_id)
          )
        `)
        .eq('school_id', profile.school_id)
        .eq('status', 'active')
        .order('start_date'),
      supabase.from('instructors')
        .select('id, rating, specialties, profile:profiles(full_name)')
        .eq('school_id', profile.school_id)
        .eq('approved', true),
    ])

    setCamps(campsData || [])
    setInstructors(instrData || [])
  }

  const handleCancelCamp = async (id) => {
    if (!window.confirm('Cancel this surf camp?')) return
    await supabase.from('surf_camps').update({ status: 'cancelled' }).eq('id', id)
    loadCamps()
  }

  const handleCreateLesson = async (e) => {
    e.preventDefault()
    setSaving(true); setError('')
    const { error } = await createLesson({ ...form, customer_id: user.id, status: 'pending' })
    if (error) setError(error.message)
    else {
      setShowCreateLesson(false)
      setForm(f => ({ ...f, scheduled_date: '', location: '' }))
      setCalendarRefreshKey(k => k + 1)
    }
    setSaving(false)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

      {/* Surf Camps */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.875rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tent size={18} color="#D4A84B" />
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-dark)', margin: 0 }}>
              Surf Camps
            </h2>
            {camps.length > 0 && (
              <span style={{ background: 'rgba(212,168,75,0.15)', color: '#8a6810', borderRadius: 20, padding: '2px 8px', fontSize: '0.72rem', fontWeight: 700 }}>
                {camps.length} active
              </span>
            )}
          </div>
          <button className="btn btn-sm" onClick={() => setShowCreateCamp(true)} style={{
            background: '#D4A84B', color: 'white', border: 'none',
            display: 'flex', alignItems: 'center', gap: 5,
          }}>
            <Plus size={14} /> New Camp
          </button>
        </div>

        {camps.length === 0 ? (
          <div style={{
            background: 'white', border: '1px dashed rgba(212,168,75,0.4)',
            borderRadius: 10, padding: '1.5rem', textAlign: 'center',
            color: 'var(--text-light)', fontSize: '0.875rem',
          }}>
            No active surf camps. Click <strong>New Camp</strong> to create one.
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '0.75rem' }}>
            {camps.map(camp => (
              <CampCard
                key={camp.id}
                camp={camp}
                instructors={instructors}
                onEdit={c => setEditCamp(c)}
                onCancel={handleCancelCamp}
              />
            ))}
          </div>
        )}
      </div>

      {/* Calendar */}
      <ScheduleCalendar onLessonCreate={() => setShowCreateLesson(true)} camps={camps} refreshKey={calendarRefreshKey} />

      {/* New Lesson Modal */}
      {showCreateLesson && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowCreateLesson(false)}>
          <div className="modal">
            <div className="modal-header">
              <h2 className="modal-title">New Lesson</h2>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowCreateLesson(false)} style={{ padding: 6 }}><X size={16} /></button>
            </div>
            <form onSubmit={handleCreateLesson}>
              <div className="modal-body">
                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label">Lesson Type</label>
                    <select className="form-control" value={form.lesson_type} onChange={set('lesson_type')}>
                      {LESSON_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Beach / Location</label>
                    <input className="form-control" type="text" placeholder="e.g. North Shore..."
                      value={form.location} onChange={set('location')} required />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Date</label>
                  <input className="form-control" type="date" required value={form.scheduled_date} onChange={set('scheduled_date')} />
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label">Start Time</label>
                    <input className="form-control" type="time" value={form.start_time} onChange={set('start_time')} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">End Time</label>
                    <input className="form-control" type="time" value={form.end_time} onChange={set('end_time')} />
                  </div>
                </div>
                <div className="grid-2">
                  <div className="form-group">
                    <label className="form-label">Students</label>
                    <input className="form-control" type="number" min={1} max={10} value={form.num_students} onChange={set('num_students')} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Price ($)</label>
                    <input className="form-control" type="number" min={0} step={5} value={form.price} onChange={set('price')} />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea className="form-control" rows={2} value={form.notes} onChange={set('notes')}
                    placeholder="Any special notes..." style={{ resize: 'vertical' }} />
                </div>
                {error && <p style={{ color: 'var(--coral)', fontSize: '0.8rem' }}>{error}</p>}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setShowCreateLesson(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Creating...' : 'Create Lesson'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Camp Modal */}
      {(showCreateCamp || editCamp) && (
        <CampModal
          onClose={() => { setShowCreateCamp(false); setEditCamp(null) }}
          onSaved={loadCamps}
          instructors={instructors}
          editCamp={editCamp || null}
        />
      )}
    </div>
  )
}
